import { useRef, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { CircleAlert, Loader2, Maximize2, X } from 'lucide-react';
import './MediaPreview.css';

export function canPreviewMedia(src, type = 'image') {
  return ['image', 'video'].includes(type) && typeof src === 'string' && Boolean(src.trim()) && !src.startsWith('preview://');
}

function PreviewContent({ src, type, title, poster, english, onClose }) {
  const closeRef = useRef(null);
  const [state, setState] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const isVideo = type === 'video';
  const name = title || (english ? (isVideo ? 'Video' : 'Image') : (isVideo ? '视频' : '图片'));
  const mediaName = isVideo ? '视频' : '图片';
  return (
    <Dialog open onClose={onClose} initialFocus={closeRef} className="media-preview"
      onKeyDown={event => {
        // 隔离底层画布、详情抽屉及聊天弹窗的快捷键。
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      }}
      onPaste={event => event.stopPropagation()}
    >
      <DialogPanel className="media-preview__panel">
        <header className="media-preview__header">
          <div><span>{english ? 'Full-screen preview' : '全屏预览'}</span><DialogTitle as="h2">{name}</DialogTitle></div>
          <button type="button" ref={closeRef} onClick={onClose} aria-label={english ? 'Close full-screen preview' : '关闭全屏预览'} title={english ? 'Close (Esc)' : '关闭（Esc）'}><X size={20} /></button>
        </header>
        <div className="media-preview__stage" aria-busy={state === 'loading'}
          onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
          {state === 'loading' && <p className="media-preview__feedback" role="status"><Loader2 size={18} className="media-preview__spinner" />{english ? `Loading ${isVideo ? 'video' : 'image'}…` : `正在加载${mediaName}…`}</p>}
          {state === 'error' ? (
            <div className="media-preview__feedback">
              <p role="alert"><CircleAlert size={18} />{english ? 'Unable to load this media. Please retry.' : `${mediaName}加载失败，请重试`}</p>
              <button type="button" onClick={() => { setState('loading'); setAttempt(value => value + 1); }}>{english ? 'Reload' : '重新加载'}</button>
            </div>
          ) : isVideo ? (
            <video key={attempt} src={src} poster={poster || undefined} controls playsInline preload="metadata" aria-label={name}
              onLoadedMetadata={() => setState('ready')} onError={() => setState('error')} />
          ) : (
            <img key={attempt} src={src} alt={name} onLoad={() => setState('ready')} onError={() => setState('error')} />
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}

// 单独挂载媒体状态，切换文件时不会沿用上一个文件的加载/失败结果。
export default function MediaPreview(props) {
  return <PreviewContent key={`${props.type}:${props.src}`} {...props} />;
}

export function MediaPreviewButton({ src, type = 'image', title, poster, english = false, children, className = '', onOpen }) {
  const [open, setOpen] = useState(false);
  if (!canPreviewMedia(src, type)) return null;
  const label = english ? `Full-screen preview ${title || ''}`.trim() : `全屏预览${title ? ` ${title}` : ''}`;
  return <>
    <button type="button" className={`media-preview-trigger ${className}`} aria-label={label} title={label}
      onPointerDown={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}
      onClick={event => { event.preventDefault(); event.stopPropagation(); onOpen?.(); setOpen(true); }}>
      {children || <Maximize2 size={16} />}
    </button>
    {open && <MediaPreview src={src} type={type} title={title} poster={poster} english={english} onClose={() => setOpen(false)} />}
  </>;
}
